import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import ko from 'knockout';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import cardTemplate from './audience-target-selector-card.pug';
import collapseTemplate from './audience-target-selector-collapse.pug';

const layoutMap = {
  card: cardTemplate,
  collapse: collapseTemplate,
};

/**
 * selectionChanged event.
 *
 * @event AudienceTargetSelector#selectionChanged
 * @type {Object}
 */

/**
 * AudienceTargetSelector control that is used to select target groups to be used.
 *
 * @class AudienceTargetSelector
 * @extends {Backbone.View}
 * @fires AudienceTargetSelector#selectionChanged
 */
export class AudienceTargetSelector extends Backbone.View {
  /**
   * Initializes the {CrudAudienceCampaign} class.
   *
   * @param {Object} selectedTypeKeys - selectedTypeKeys
   * @param {Array} blackListKeys - blackListKeys
   * @param {Array} lockedKeys - the keys always selected
   * @param {string} level - campaign or adgroup
   * @returns {undefined}
   */
  initialize({
    selectedTypeKeys,
    blackListKeys = [],
    lockedKeys = [],
    level = 'adgroup',
    activityLogger,
    layout = 'card',
    permissions,
  } = {}) {
    this.level = level;
    this.layout = layout;
    this.activityLogger = activityLogger;

    if (this.activityLogger) {
      if (_.isArray(lockedKeys) && !_.isEmpty(lockedKeys) &&
        _.isArray(blackListKeys) && !_.isEmpty(blackListKeys)) {
        const conflictingKeys = _.intersection(lockedKeys, blackListKeys);
        if (!_.isEmpty(conflictingKeys)) {
          this.activityLogger.signal(`conflicting audience target categories. lockedKeys: ${lockedKeys}; blackListKeys: ${blackListKeys}`, 'audience target selector');
        }
      }
    }

    const Enable1PPlacementTargeting = permissions && permissions.dynamic && permissions.dynamic.Enable1PPlacementTargeting;
    const ContentTargeting = permissions && permissions.dynamic && permissions.dynamic.ContentTargeting;

    this.selectionStates = _.mapObject({
      location: selectedTypeKeys ?
        (_.contains(selectedTypeKeys, 'Location') || _.contains(selectedTypeKeys, 'Radius') || _.contains(selectedTypeKeys, 'NegativeLocation')) : false,
      age: selectedTypeKeys ? _.contains(selectedTypeKeys, 'Age') : false,
      gender: selectedTypeKeys ? _.contains(selectedTypeKeys, 'Gender') : false,
      device: selectedTypeKeys ? _.contains(selectedTypeKeys, 'Device') : false,
      audience: selectedTypeKeys ? _.contains(selectedTypeKeys, 'Audience') : false,
      companyName: selectedTypeKeys ? _.contains(selectedTypeKeys, 'CompanyName') : false,
      industry: selectedTypeKeys ? _.contains(selectedTypeKeys, 'Industry') : false,
      jobFunction: selectedTypeKeys ? _.contains(selectedTypeKeys, 'JobFunction') : false,
    }, (value, key) => value || _.contains(lockedKeys, key));

    const i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.i18n = i18n;
    if (Enable1PPlacementTargeting) {
      this.selectionStates.placement = selectedTypeKeys ? _.contains(selectedTypeKeys, 'Placement') : _.contains(lockedKeys, 'Placement');
    }

    this.items = _.reject(_.compact([{
      id: 'location',
      title: i18n.getString('Audience_Campaign_Target_Selection_Location_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Location_Description'),
      popUpKey: 'pop_BA_CampaignCreate_Settings_LocationTargeting',
      popUpTitle: 'Location_Target_Header_Title ',
    },
    {
      id: 'age',
      title: i18n.getString('Audience_Campaign_Target_Selection_Age_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Age_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_Age',
      popUpTitle: 'Audience_Campaign_Target_Setting_Age_Title ',
    },
    {
      id: 'gender',
      title: i18n.getString('Audience_Campaign_Target_Selection_Gender_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Gender_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_Gender',
      popUpTitle: 'Audience_Campaign_Target_Setting_Gender_Title',
    },
    {
      id: 'device',
      title: i18n.getString('Audience_Campaign_Target_Selection_Device_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Device_Description'),
      popUpKey: 'pop_BA_CampaignCreate_Settings_Device',
      popUpTitle: 'Audience_Campaign_Target_Setting_Device_Title',
    },
    {
      id: 'audience',
      title: i18n.getString('Audience_Campaign_Target_Selection_Audience_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Audiene_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_Audience',
      popUpTitle: 'Audience_Campaign_Target_Setting_Audience_Title',
    },
    {
      id: 'companyName',
      title: i18n.getString('Audience_Campaign_Target_Selection_Company_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Company_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_Company',
      popUpTitle: 'AudienceCampaign_CompanyNameAddCriterionTitleLabel',
    },
    {
      id: 'industry',
      title: i18n.getString('Audience_Campaign_Target_Selection_Industry_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Industry_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_Industry',
      popUpTitle: 'AudienceCampaign_IndustryAddCriterionTitleLabel',
    },
    {
      id: 'jobFunction',
      title: i18n.getString('Audience_Campaign_Target_Selection_Job_Function_Title'),
      description: i18n.getString('Audience_Campaign_Target_Selection_Job_Function_Description'),
      popUpKey: 'pop_BA_CampaignCreate_AudTargets_JobFunction',
      popUpTitle: 'AudienceCampaign_JobFunctionAddCriterionTitleLabel',
    },
    Enable1PPlacementTargeting && !ContentTargeting && {
      id: 'placement',
      title: i18n.getString(_TL_('Placements')),
      description: i18n.getString(_TL_('Show your ads on specific Microsoft owned and operated placements.')),
      popUpKey: 'pop_BA_AdPlacements',
      popUpTitle: 'AudienceCampaign_PlacementCriterionTitleLabel',
    },
    ]), item => _.contains(blackListKeys, item.id));

    this.dataModel = {
      locationSummaryText: ko.observable(''),
      ageSummaryText: ko.observable(''),
      genderSummaryText: ko.observable(''),
      audienceSummaryText: ko.observable(''),
      companyNameSummaryText: ko.observable(''),
      industrySummaryText: ko.observable(''),
      jobFunctionSummaryText: ko.observable(''),
    };
    if (Enable1PPlacementTargeting) {
      this.dataModel.placementSummaryText = ko.observable('');
    }
  }

  /**
   * Define events and event handlers for them.
   *
   * @readonly
   * @returns {Object} returns object defining events
   */
  get events() {
    return {
      'click .selection-item-button': this.onSelectionClicked,
      'show.bs.collapse .collapse-container': this.onSelectorShow,
      'hide.bs.collapse .collapse-container': this.onSelectorHide,
    };
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    this.$el.html(layoutMap[this.layout](this));
    ko.applyBindings(this.dataModel, this.el);

    return this;
  }

  filterTargetItems(id) {
    this.items = _.filter(this.items, item => item.id === id);
  }

  onSelectorShow(e) {
    $(e.target).parent()
      .find('.summary-details')
      .css('visibility', 'hidden');
  }

  onSelectorHide(e) {
    $(e.target).parent()
      .find('.summary-details')
      .css('visibility', 'visible');
  }

  // private function
  // handles the click event for the selection
  onSelectionClicked(e) {
    if (_.isUndefined(e) || _.isUndefined(e.currentTarget)) {
      throw new Error('unexpected event from the click', e);
    }

    // Stop the propagation to disable the toggle active state.
    e.stopImmediatePropagation();

    const { id } = e.currentTarget;

    this.selectionStates[id] = true;

    $(e.currentTarget).find('.glyphicon').removeClass('glyphicon-plus');
    $(e.currentTarget).find('.glyphicon').addClass('glyphicon-ok');
    $(e.currentTarget).addClass('active');
    $(e.currentTarget).attr('aria-checked', 'true');


    if (this.activityLogger) {
      this.activityLogger.signal(`audience target selected: ${id}`, 'audience target selector');
    }

    this.trigger('selectionChanged', id);
  }

  turnOnAudienceTarget() {
    const audienceBtn = $('.btn.btn-primary.selection-item-button#audience');
    if (audienceBtn) {
      this.selectionStates.audience = true;
      audienceBtn.find('.glyphicon').removeClass('glyphicon-plus');
      audienceBtn.find('.glyphicon').addClass('glyphicon-ok');
      audienceBtn.addClass('active');
      audienceBtn.attr('aria-checked', 'true');
    }
  }
}
